import { skipToken } from '@reduxjs/toolkit/query/react';
import { AlertTriangle, ChevronDown, Crown, EllipsisVertical, ExternalLink } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { StatusPill } from '@/components/common/DataTable/StatusPill';
import { useAutoChase } from '@/hooks/useAutoChase';
import {
  useGetIntakeQuery,
  useSetNoMatterNeededMutation,
} from '@/services/api/intakeService';
import { IntakeHandlerValue } from '@/services/types/intake-types';

import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { HandlerToggle } from './components/HandlerToggle';
import { StatusSelect } from './components/StatusSelect';
import { SaveStatus } from './evidence-review-dialog';
import { ReassignIntakeModal } from './ReassignIntakeModal';

interface Props {
  title: string;
  intakeId?: string | null;
  status: string;
  handler?: IntakeHandlerValue;
  onSave: () => void;
  isSaving: boolean;
  matterId: string | null;
  matterStatus?: string | null;
  isNew: boolean;
  canExport: boolean;
  isProcessing: boolean;
  exportFailed: boolean;
  downloadTimestamp?: string | null;
  handleStartExport: () => void;
  canDownload: boolean;
  handleDownload: () => void;
  canConvert: boolean;
  isConverting: boolean;
  handleConvert: () => void | Promise<unknown>;
  isArchived: boolean;
  isArchiving: boolean;
  handleArchiveToggle: () => void | Promise<unknown>;
  isAutoSaving?: boolean;
  lastAutoSaveTime?: Date | null;
  autoSaveError?: string | null;
  hasUnsavedChanges?: boolean;
  errorCount?: number;
  preexportNeeds?: { needsReview: boolean; needsSummary: boolean };
  onGoToStage?: (stage: 'collect' | 'review' | 'summary') => void;
  isLeadSourceMissing?: boolean;
  isReferralSourceMissing?: boolean;
  isRejectedOrUnqualified?: boolean;
  hasLinkedLead?: boolean;
  linkedLeadStatus?: string | null;
}

export const IntakeHeader: React.FC<Props> = ({
  title,
  intakeId,
  status,
  handler,
  onSave,
  isSaving,
  matterId,
  matterStatus,
  isNew,
  canExport,
  isProcessing,
  exportFailed,
  downloadTimestamp,
  handleStartExport,
  canDownload,
  handleDownload,
  canConvert,
  isConverting,
  handleConvert,
  isArchived,
  isArchiving,
  handleArchiveToggle,
  isAutoSaving,
  lastAutoSaveTime,
  autoSaveError,
  hasUnsavedChanges,
  errorCount,
  preexportNeeds,
  onGoToStage,
  isLeadSourceMissing,
  isReferralSourceMissing,
  isRejectedOrUnqualified,
  hasLinkedLead,
  linkedLeadStatus,
}) => {
  const navigate = useNavigate();
  const [exportPopoverOpen, setExportPopoverOpen] = React.useState(false);
  const [rejectedWarningOpen, setRejectedWarningOpen] = React.useState(false);
  const [reassignModalOpen, setReassignModalOpen] = React.useState(false);
  const [setNoMatterNeeded, { isLoading: isSettingNoMatterNeeded }] =
    useSetNoMatterNeededMutation();

  // Fetch intake detail to use computed companion fields
  const { data: intakeDetail } = useGetIntakeQuery(intakeId || skipToken, {
    skip: !intakeId,
  });

  // Check if there's an active chase for the stop-confirmation dialog
  const { canStop: hasActiveChase } = useAutoChase(intakeId || '');

  const isNoMatterNeeded = Boolean(intakeDetail?.no_matter_needed);

  const handleNoMatterNeededToggle = React.useCallback(async () => {
    if (!intakeId) return;
    try {
      await setNoMatterNeeded({
        intakeId,
        value: !isNoMatterNeeded,
      }).unwrap();
    } catch (err) {
      /* eslint-disable no-console */
      console.error('Failed to toggle no matter needed', err);
    }
  }, [intakeId, isNoMatterNeeded, setNoMatterNeeded]);

  const isPrimary = Boolean(intakeDetail?.is_companion_primary);
  const memberCount = intakeDetail?.companion_count ?? 0;
  const companionRoot = intakeDetail?.companion_root ?? null;

  // Build companion navigation list from form_data
  const companionRows = React.useMemo(() => {
    const rows = intakeDetail?.form_data?.companions ?? [];
    return rows.filter(
      (r) => r.client_name?.trim() && r.created_intake_id,
    );
  }, [intakeDetail?.form_data?.companions]);

  // A matter is "promoted" when it exists and is no longer in INTAKE status
  const isMatterPromoted = Boolean(matterId && matterStatus !== 'Intake');

  // Helper to decide labels for the conversion button
  const getMatterActionLabel = () => {
    if (isMatterPromoted) return 'Sync to Matter';
    if (matterStatus === 'Intake') return 'Activate Matter';
    return 'Create Matter';
  };
  const matterActionLabel = getMatterActionLabel();

  const handleExportClick = () => {
    const needsReview = Boolean(preexportNeeds?.needsReview);
    const needsSummary = Boolean(preexportNeeds?.needsSummary);
    if (!needsReview && !needsSummary) {
      handleStartExport();
      return;
    }
    setExportPopoverOpen(true);
  };

  const renderArchiveLabel = () => {
    if (isArchiving) {
      return isArchived ? 'Un-archiving…' : 'Archiving…';
    }
    return isArchived ? 'Un-archive' : 'Archive';
  };

  // Warning: intake is open but linked lead is cancelled/closed
  const OPEN_INTAKE_STATUSES = ['in_progress', 'sent_for_client_signature', 'signed', 'chase'];
  const CLOSED_LEAD_STATUSES = ['closed', 'rejected'];
  const normalizedIntakeStatus = status?.toLowerCase().replace(/\s+/g, '_') ?? '';
  const showLeadStatusWarning =
    hasLinkedLead &&
    OPEN_INTAKE_STATUSES.includes(normalizedIntakeStatus) &&
    CLOSED_LEAD_STATUSES.includes(linkedLeadStatus?.toLowerCase() ?? '');

  return (
    <div className="flex flex-col gap-2">
      {showLeadStatusWarning && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span>
            This intake is still open but the linked lead has been{' '}
            <strong>{linkedLeadStatus?.toLowerCase()}</strong> in Lexamica.
            Please review and update the intake status accordingly.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
      {/* Left side: Client info */}
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
          {memberCount >= 2 && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="group inline-flex items-center gap-1.5 rounded-full bg-zinc-900 pl-2.5 pr-2 py-1 cursor-pointer transition-all duration-200 hover:bg-zinc-800 active:scale-[0.97]"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white">
                    {memberCount} Pack
                  </span>
                  {isPrimary && (
                    <span className="flex items-center justify-center h-4 w-4 rounded-full bg-amber-400">
                      <Crown className="h-2.5 w-2.5 text-zinc-900" />
                    </span>
                  )}
                  <ChevronDown className="h-3 w-3 text-zinc-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="bottom"
                className="w-72 rounded-2xl border border-zinc-200/80 bg-white p-0 shadow-lg"
              >
                {/* Header */}
                <div className="px-4 pt-3.5 pb-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                      Companion Cases
                    </span>
                    <span className="flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-zinc-100 text-[10px] font-bold text-zinc-600">
                      {memberCount}
                    </span>
                  </div>
                </div>
                <div className="h-px bg-zinc-100" />

                {/* Members list */}
                <div className="py-1.5">
                  {/* Current intake */}
                  <div className="mx-1.5 flex items-center gap-2.5 rounded-xl bg-zinc-50 px-3 py-2.5">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900 shrink-0">
                      <span className="text-[10px] font-bold text-white">
                        {title.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-zinc-900 truncate">{title}</span>
                        {isPrimary && (
                          <span className="flex items-center gap-0.5 rounded-full bg-amber-400/15 px-1.5 py-0.5">
                            <Crown className="h-2.5 w-2.5 text-amber-600" />
                            <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700">Primary</span>
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-medium text-zinc-500">Current intake</span>
                    </div>
                  </div>

                  {/* Companion intakes (primary sees the list) */}
                  {isPrimary && companionRows.map((row) => (
                    <button
                      key={row.created_intake_id}
                      type="button"
                      onClick={() => navigate(`/intakes/${row.created_intake_id}`)}
                      className="mx-1.5 mt-0.5 flex w-[calc(100%-12px)] items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors duration-150 hover:bg-zinc-50 cursor-pointer group/item"
                    >
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-200 shrink-0 transition-colors duration-150 group-hover/item:bg-zinc-300">
                        <span className="text-[10px] font-bold text-zinc-600">
                          {row.client_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <span className="text-sm font-medium text-zinc-900 truncate block">{row.client_name}</span>
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 text-zinc-400 opacity-0 transition-opacity duration-150 group-hover/item:opacity-100 shrink-0" />
                    </button>
                  ))}

                  {/* Non-primary: link to primary */}
                  {!isPrimary && companionRoot && (
                    <button
                      type="button"
                      onClick={() => navigate(`/intakes/${companionRoot}`)}
                      className="mx-1.5 mt-0.5 flex w-[calc(100%-12px)] items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors duration-150 hover:bg-zinc-50 cursor-pointer group/item"
                    >
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400/15 shrink-0">
                        <Crown className="h-3.5 w-3.5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <span className="text-sm font-medium text-zinc-900 block">View Primary Intake</span>
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 text-zinc-400 opacity-0 transition-opacity duration-150 group-hover/item:opacity-100 shrink-0" />
                    </button>
                  )}
                </div>

                {/* Footer hint */}
                <div className="h-px bg-zinc-100" />
                <div className="px-4 py-2.5">
                  <span className="text-[10px] font-medium text-zinc-500">
                    {isPrimary
                      ? 'You are viewing the primary intake'
                      : 'This intake is a companion case'}
                  </span>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {/* Mobile: read-only pill */}
          <StatusPill status={status} className="md:hidden" />
          {/* Desktop: inline status updater and handler toggle */}
          {intakeId ? (
            <div className="hidden md:flex items-center gap-2">
              <StatusSelect intakeId={intakeId} value={status} hasActiveChase={hasActiveChase} hasLinkedLead={hasLinkedLead} />
              <HandlerToggle intakeId={intakeId} value={handler} />
            </div>
          ) : null}
        </div>
      </div>

      {/* Right side: Action Buttons */}
      <div className="flex items-center gap-2">
        {/* AutoSave Status */}
        <span className="text-xs">
          <SaveStatus
            isAutoSaving={!!isAutoSaving}
            lastAutoSaveTime={lastAutoSaveTime ?? null}
            autoSaveError={autoSaveError ?? null}
            hasUnsavedChanges={hasUnsavedChanges}
            errorCount={errorCount}
            showIcon
            size="sm"
          />
        </span>

        {/* Save Button - primary and first since it's used most frequently */}
        <Button
          onClick={onSave}
          size="sm"
          disabled={isSaving}
          tabIndex={0}
          className="px-4 py-1.5"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>

        {/* Export */}
        <Popover open={exportPopoverOpen} onOpenChange={setExportPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="text-xs px-3 py-1.5"
              tabIndex={0}
              disabled={!canExport || isProcessing}
              onClick={handleExportClick}
              aria-label="Export to PDF"
            >
              {isProcessing ? 'Processing…' : 'Export to PDF'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" side="bottom" className="w-80">
            {preexportNeeds?.needsReview || preexportNeeds?.needsSummary ? (
              <div className="space-y-2 text-sm">
                <div className="font-semibold text-gray-900">
                  Before exporting
                </div>
                {preexportNeeds?.needsReview && (
                  <div className="text-gray-700">
                    Review has items that need more detail.{' '}
                    <button
                      type="button"
                      className="text-blue-600 underline"
                      onClick={() => {
                        setExportPopoverOpen(false);
                        onGoToStage?.('review');
                      }}
                    >
                      Go to Review
                    </button>
                  </div>
                )}
                {preexportNeeds?.needsSummary && (
                  <div className="text-gray-700">
                    Summary is incomplete.{' '}
                    <button
                      type="button"
                      className="text-blue-600 underline"
                      onClick={() => {
                        setExportPopoverOpen(false);
                        onGoToStage?.('summary');
                      }}
                    >
                      Go to Summary
                    </button>
                  </div>
                )}
                <div className="pt-1">
                  <button
                    type="button"
                    className="text-gray-700 underline"
                    onClick={() => {
                      setExportPopoverOpen(false);
                      handleStartExport();
                    }}
                  >
                    Export anyway
                  </button>
                </div>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>

        {/* View Matter - show whenever a matter exists (even in INTAKE status) */}
        {!isNew && matterId && (
          <Button
            onClick={() => navigate(`/matters/${matterId}`)}
            variant="outline"
            size="sm"
            className="text-xs px-3 py-1.5"
            tabIndex={0}
          >
            View Matter
          </Button>
        )}

        {/* Create / Sync Matter Button */}
        {(matterId || canConvert || isLeadSourceMissing || isReferralSourceMissing) && (
          <Popover open={rejectedWarningOpen} onOpenChange={setRejectedWarningOpen}>
            <Tooltip open={(isLeadSourceMissing || isReferralSourceMissing) && !isMatterPromoted ? undefined : false}>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    onClick={(e) => {
                      if (isRejectedOrUnqualified && !isMatterPromoted) {
                        e.preventDefault();
                        setRejectedWarningOpen(true);
                        return;
                      }
                      e.preventDefault();
                      handleConvert();
                    }}
                    variant="outline"
                    size="sm"
                    disabled={isMatterPromoted ? isConverting : !canConvert || isConverting}
                    className="text-xs px-3 py-1.5"
                    tabIndex={0}
                  >
                    {isConverting ? 'Syncing…' : matterActionLabel}
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              {(isLeadSourceMissing || isReferralSourceMissing) && !isMatterPromoted && (
                <TooltipContent>
                  <p className="text-sm font-medium">
                    {isLeadSourceMissing && isReferralSourceMissing
                      ? 'Lead Source and Referral Source are required to create a matter'
                      : isLeadSourceMissing
                        ? 'Lead Source is required to create a matter'
                        : 'Referral Source is required to create a matter'}
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
            <PopoverContent align="end" side="bottom" className="w-80">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                  <span className="text-sm font-medium text-gray-900">
                    This intake is rejected / unqualified
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Creating a matter will incur operational costs on a case that may not be worked on.
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => setRejectedWarningOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    className="text-xs"
                    onClick={() => {
                      setRejectedWarningOpen(false);
                      handleConvert();
                    }}
                  >
                    {matterActionLabel} Anyway
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Dropdown for secondary actions */}
        <DropdownMenu>
          <DropdownMenuTrigger aria-label="More actions" asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-xs px-2 py-1.5"
              tabIndex={0}
            >
              <EllipsisVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canDownload ? (
              <DropdownMenuItem
                onSelect={handleDownload}
                aria-label="Download PDF"
              >
                {`Download PDF${downloadTimestamp ? ` (${new Date(downloadTimestamp).toLocaleString()})` : ''}`}
              </DropdownMenuItem>
            ) : null}
            {exportFailed && !canDownload ? (
              <DropdownMenuItem disabled aria-label="Latest export failed">
                Latest export failed
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onSelect={handleArchiveToggle}
              disabled={isArchiving}
              aria-label={isArchived ? 'Un-archive' : 'Archive'}
            >
              {renderArchiveLabel()}
            </DropdownMenuItem>
            {intakeId && (
              <DropdownMenuItem
                onSelect={handleNoMatterNeededToggle}
                disabled={isSettingNoMatterNeeded}
                aria-label={isNoMatterNeeded ? 'Clear No Matter Needed' : 'No Matter Needed'}
              >
                {isSettingNoMatterNeeded
                  ? 'Updating…'
                  : isNoMatterNeeded
                    ? 'Clear No Matter Needed'
                    : 'No Matter Needed'}
              </DropdownMenuItem>
            )}
            {intakeId && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => setReassignModalOpen(true)}
                  aria-label="Reassign to Matter"
                >
                  Reassign to Matter
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Reassign Intake Modal */}
        {intakeId && (
          <ReassignIntakeModal
            intakeId={intakeId}
            currentMatterId={matterId}
            isOpen={reassignModalOpen}
            onClose={() => setReassignModalOpen(false)}
          />
        )}
      </div>
      </div>
    </div>
  );
};
